import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, getPassengersByAdmin } from '../redux/slices/userSlice'; // Adjust path based on your project structure

const UsersPage = () => {
    const dispatch = useDispatch();
    const { usersList, loading, error, selectedPassengerList } = useSelector((state) => state.user);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [passengerModalOpen, setPassengerModalOpen] = useState(false);
    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [address, setAddress] = useState(null);
    const [modalError, setModalError] = useState(null);

    useEffect(() => {
        dispatch(getAllUsers());
    }, [dispatch]);

    const handleViewPassengers = async (userId) => {
        setSelectedUserId(userId);
        setModalError(null);
        try {
            console.log('Fetching passengers for userId:', userId); // Debug log
            await dispatch(getPassengersByAdmin({ userId })).unwrap();
            setPassengerModalOpen(true);
        } catch (err) {
            setModalError(err || 'Failed to fetch passengers');
        }
    };

    const handleViewAddress = async (userId) => {
        setSelectedUserId(userId);
        setModalError(null);
        try {
            const user = usersList.find((u) => u._id === userId);
            if (user && user.address) {
                setAddress(user.address);
                setAddressModalOpen(true);
            } else {
                setModalError('No address found for this user');
            }
        } catch (err) {
            setModalError('Failed to fetch address');
        }
    };

    const closeModals = () => {
        setPassengerModalOpen(false);
        setAddressModalOpen(false);
        setSelectedUserId(null);
        setAddress(null);
        setModalError(null);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-custom-white">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-custom-yellow"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen bg-custom-white">
                <div className="text-custom-blue text-xl font-semibold bg-red-100 p-4 rounded-lg">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-custom-white py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-4xl font-bold text-custom-blue mb-8 text-center">
                    User Management
                </h1>
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-custom-blue text-custom-white">
                                <tr>
                                    <th className="py-3 px-6 text-left text-sm font-medium uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="py-3 px-6 text-left text-sm font-medium uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="py-3 px-6 text-left text-sm font-medium uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th className="py-3 px-6 text-left text-sm font-medium uppercase tracking-wider">
                                        Created At
                                    </th>
                                    <th className="py-3 px-6 text-left text-sm font-medium uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {usersList.map((user) => (
                                    <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-6 text-custom-blue">{user.name}</td>
                                        <td className="py-4 px-6 text-custom-blue">{user.email}</td>
                                        <td className="py-4 px-6 text-custom-blue">{user.role}</td>
                                        <td className="py-4 px-6 text-custom-blue">
                                            {new Date(user.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="py-4 px-6">
                                            <button
                                                onClick={() => handleViewPassengers(user._id)}
                                                className="bg-custom-yellow text-custom-blue px-4 py-2 rounded-md font-medium hover:bg-yellow-500 transition-colors mr-2"
                                            >
                                                View Passengers
                                            </button>
                                            <button
                                                onClick={() => handleViewAddress(user._id)}
                                                className="bg-custom-blue text-custom-white px-4 py-2 rounded-md font-medium hover:bg-blue-800 transition-colors"
                                            >
                                                View Address
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Passenger Modal */}
                {passengerModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
                        <div className="bg-custom-white p-8 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
                            <h2 className="text-3xl font-bold text-custom-blue mb-6">
                                Passenger List
                            </h2>
                            {modalError ? (
                                <p className="text-red-600 bg-red-100 p-4 rounded-md mb-4">
                                    {modalError}
                                </p>
                            ) : !Array.isArray(selectedPassengerList) ||
                                selectedPassengerList.length === 0 ? (
                                <p className="text-custom-blue text-lg">
                                    No passengers found for this user.
                                </p>
                            ) : (
                                <ul className="space-y-4">
                                    {selectedPassengerList.map((passenger, index) => (
                                        <li
                                            key={index}
                                            className="border-b border-gray-200 pb-4 last:border-b-0"
                                        >
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <p>
                                                    <strong className="text-custom-blue">First Name:</strong>{' '}
                                                    {passenger.firstName || 'N/A'}
                                                </p>
                                                <p>
                                                    <strong className="text-custom-blue">Last Name:</strong>{' '}
                                                    {passenger.lastName || 'N/A'}
                                                </p>
                                                <p>
                                                    <strong className="text-custom-blue">Email:</strong>{' '}
                                                    {passenger.email || 'N/A'}
                                                </p>
                                                <p>
                                                    <strong className="text-custom-blue">Phone:</strong>{' '}
                                                    {passenger.phoneCountry || ''}{' '}
                                                    {passenger.phoneNumber || 'N/A'}
                                                </p>
                                                <p>
                                                    <strong className="text-custom-blue">Gender:</strong>{' '}
                                                    {passenger.gender || 'N/A'}
                                                </p>
                                                <p>
                                                    <strong className="text-custom-blue">Date of Birth:</strong>{' '}
                                                    {passenger.dob
                                                        ? new Date(passenger.dob).toLocaleDateString()
                                                        : 'N/A'}
                                                </p>
                                                <p>
                                                    <strong className="text-custom-blue">Trips:</strong>{' '}
                                                    {passenger.trips || 0}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <button
                                onClick={closeModals}
                                className="mt-6 w-full bg-custom-blue text-custom-white px-4 py-2 rounded-md font-medium hover:bg-blue-800 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}

                {/* Address Modal */}
                {addressModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
                        <div className="bg-custom-white p-8 rounded-lg max-w-lg w-full shadow-2xl">
                            <h2 className="text-3xl font-bold text-custom-blue mb-6">
                                User Address
                            </h2>
                            {modalError ? (
                                <p className="text-red-600 bg-red-100 p-4 rounded-md mb-4">
                                    {modalError}
                                </p>
                            ) : address ? (
                                <div className="space-y-2">
                                    <p>
                                        <strong className="text-custom-blue">Address Line:</strong>{' '}
                                        {address.addressLine || 'N/A'}
                                    </p>
                                    <p>
                                        <strong className="text-custom-blue">City:</strong>{' '}
                                        {address.city || 'N/A'}
                                    </p>
                                    <p>
                                        <strong className="text-custom-blue">State:</strong>{' '}
                                        {address.state || 'N/A'}
                                    </p>
                                    <p>
                                        <strong className="text-custom-blue">Country:</strong>{' '}
                                        {address.country || 'N/A'}
                                    </p>
                                    <p>
                                        <strong className="text-custom-blue">Zip:</strong>{' '}
                                        {address.zip || 'N/A'}
                                    </p>
                                </div>
                            ) : (
                                <p className="text-custom-blue text-lg">
                                    No address found for this user.
                                </p>
                            )}
                            <button
                                onClick={closeModals}
                                className="mt-6 w-full bg-custom-blue text-custom-white px-4 py-2 rounded-md font-medium hover:bg-blue-800 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UsersPage;